import React, { useState, useEffect } from 'react';
import Dashboard from '../components/PortBitumen/Dashboard';
import VesselList from '../components/PortBitumen/VesselList';
import { SmartEntry } from '../components/PortBitumen/SmartEntry';
import ConsolidatedView from '../components/PortBitumen/ConsolidatedView';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { LayoutGrid, Ship, Sparkles, Database, Anchor, RefreshCw, FileText, AlertTriangle, ShieldCheck } from 'lucide-react';
import { Vessel, User, Country, PortName, BitumenGrade } from '../types';
import { apiService } from '../services/apiService';

const PortBitumenTrackerModule: React.FC<{ currentUser: User; activeCountry: Country }> = ({ currentUser, activeCountry }) => {
  const [view, setView] = useState<'REGISTRY' | 'CONSOLIDATED' | 'ENTRY'>('CONSOLIDATED');
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Deletion Confirmation State
  const [vesselToDelete, setVesselToDelete] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const data = await apiService.getVessels();
      setVessels(data);
    } catch (e) {
      console.error("Failed to load vessels", e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateVessel = async (updatedVessel: Vessel) => {
    await apiService.saveVessel(updatedVessel);
    setVessels(prev => prev.map(v => v.id === updatedVessel.id ? updatedVessel : v));
  };

  const handleConfirmDelete = async () => {
    if (!vesselToDelete) return;
    
    try {
      const success = await apiService.deleteVessel(vesselToDelete);
      if (success) {
          setVessels(prev => prev.filter(v => v.id !== vesselToDelete));
      }
    } catch (e) {
      console.error("Deletion Failed", e);
      alert("System security error: Could not revoke registry record.");
    } finally {
      setVesselToDelete(null);
    }
  };

  return (
    <div className="flex flex-col h-full animate-in fade-in duration-500">
      {/* Permanent Revocation Handshake */}
      <ConfirmationDialog 
        isOpen={!!vesselToDelete}
        title="Revoke Vessel Registry"
        message="This will permanently delete the vessel profile and all its associated manifest records from the master port database. This action is audited."
        confirmText="Confirm Delete"
        cancelText="Discard"
        isDestructive={true}
        onConfirm={handleConfirmDelete}
        onCancel={() => setVesselToDelete(null)}
      />

      <div className="bg-white border-b border-slate-200 px-8 flex items-center justify-between sticky top-0 z-20 shrink-0 shadow-sm">
        <div className="flex gap-8">
          {[
            { id: 'REGISTRY', label: 'Intelligence & Registry', icon: LayoutGrid },
            { id: 'CONSOLIDATED', label: 'Market Analytics', icon: FileText },
            { id: 'ENTRY', label: 'Logistics Ingestion', icon: Sparkles }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setView(tab.id as any)}
              className={`py-4 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 border-b-2 transition-all ${
                view === tab.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4">
            <button 
                onClick={loadData}
                className="p-2 text-slate-300 hover:text-indigo-600 transition-colors"
                title="Synchronize Node"
            >
                <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
            </button>
            <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest bg-slate-50 px-4 py-1.5 rounded-full border border-slate-100 flex items-center gap-2">
                <Anchor size={12} className="text-indigo-400" />
                Registry Active: {activeCountry} Sector
            </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
        {view === 'REGISTRY' && (
          <div className="space-y-12 max-w-full mx-auto">
            <Dashboard vessels={vessels} />
            <div className="border-t border-slate-200 pt-12">
              <VesselList 
                vessels={vessels} 
                onDelete={setVesselToDelete} 
                onUpdate={handleUpdateVessel} 
                currentUser={currentUser}
              />
            </div>
          </div>
        )}
        {view === 'CONSOLIDATED' && (
           <div className="max-w-full mx-auto">
              <ConsolidatedView vessels={vessels} />
           </div>
        )}
        {view === 'ENTRY' && (
          <div className="w-full h-full">
            <SmartEntry onSuccess={() => { setView('REGISTRY'); loadData(); }} currentPort={activeCountry === Country.TANZANIA ? PortName.DAR_ES_SALAAM : PortName.MOMBASA} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PortBitumenTrackerModule;